import { SyncOutlined } from '@ant-design/icons';
import { Button, Card, Col, Row, Spin, Timeline } from 'antd';
import { TriggerCollapse } from 'components/hitches/online-store/TriggerCollapse';
import capitalize from 'components/utils/capitalize';
import { formatRelative } from 'date-fns';
import React from 'react';
import { useIntl } from 'react-intl';
import useHikeRelatedEvents from 'services/firebase/hooks/useHikeRelatedEvents';
import { Cause, HikeEventStatus, TriggerEvent } from 'services/hitchit/types/hike/HikeEvents';
import { ResourceLinkedEvent, TriggerResource } from 'services/hitchit/types/hike/ResourceEvents';
import ResourceEventView from '../ResourceEventView';

type LastEventProps = { hikeId: string; limit?: number };

function createTriggerLabel(cause: Cause, hitch: string): string {
  switch (cause.type) {
    case 'manual':
      return `Triggered from Hitch and Hike dashboard `;
    case 'webhook':
      return `Triggered by ${capitalize(hitch)}, due to ${cause.event}`;
    case 'preview':
      return `Triggered during configuration `;
    case 'machine':
      return `Triggered by Hitch and Hike support`;
    default:
      return `Triggered by: ${cause.type} - ${cause.event}`;
  }
}

function pickColor(event: HikeEventStatus): string {
  switch (event) {
    case 'new':
    case 'busy':
      return 'blue';
    case 'failed':
      return 'red';
    case 'success':
      return 'green';
    default:
      return 'gray';
  }
}

function removeDuplicatesWhenSomeWithNoChanges(resourceLinkedEvents: ResourceLinkedEvent[]) {
  const resourcesWithChanges = resourceLinkedEvents.filter(
    (event) => event.resource.type !== 'none'
  );

  return resourceLinkedEvents.filter((event) => {
    return (
      event.resource.type !== 'none' ||
      !resourcesWithChanges.find(
        (withChangesEvent) =>
          withChangesEvent.hitch === event.hitch &&
          withChangesEvent.resource.id === event.resource.id &&
          withChangesEvent.resource.resource === event.resource.resource
      )
    );
  });
}

const HikeEventsTimeLine = ({ events }: { events: TriggerEvent[] }): JSX.Element => {
  const { formatMessage } = useIntl();
  if (events.length === 0) {
    return (
      <div>
        <p>
          <SyncOutlined style={{ marginRight: 20 }} />
          {formatMessage({
            defaultMessage:
              'No one likes an empty dashboard. Let us work for you to see some movement here.',
            description: 'triggers/no-orders/description',
          })}
        </p>
      </div>
    );
  }
  return (
    <Timeline>
      {events
        // don't show machine cause events
        .filter((event) => event.cause.type !== 'machine')
        .map((event: TriggerEvent) => {
          const color = pickColor(event.status);
          const label = createTriggerLabel(event.cause, event.triggerHitch);
          const orderOrProduct = JSON.parse((event.resource.resource as TriggerResource).data);

          if (!orderOrProduct.type) {
            orderOrProduct.type = 'order';
          }
          return (
            <Spin key={event.id} spinning={event.status === 'busy'}>
              <Timeline.Item key={event.id} color={color}>
                <p>{capitalize(formatRelative(event.createdAt, new Date()))}</p>
                <Row gutter={[32, 32]} justify="space-around">
                  <Col>
                    {event?.result ? (
                      Object.entries(event.result).map(([resourceType, resourceResult]) => (
                        <ResourceEventView
                          hikeId={event.hike.id}
                          triggerEventId={event.id}
                          key={`${JSON.stringify(resourceType)}`}
                          resourceType={resourceType}
                          result={resourceResult}
                          resourceEvent={
                            event.linkedResource &&
                            removeDuplicatesWhenSomeWithNoChanges(event.linkedResource).find(
                              (resource) => resource.resource.resource === resourceType
                            )
                          }
                        />
                      ))
                    ) : (
                      <Card>No changes noticed (yet)</Card>
                    )}
                  </Col>
                  <Col>
                    <Card
                      title={
                        <h3>{`Original ${event.resource.resource.resource} from ${capitalize(
                          event.triggerHitch
                        )}`}</h3>
                      }
                    >
                      <Col>
                        <h4>{label}</h4>
                      </Col>
                      <TriggerCollapse trigger={orderOrProduct} />
                      {orderOrProduct.url && (
                        <a target="_blank" href={orderOrProduct.url} rel="noreferrer">
                          <Button type="primary">View order in Shopify</Button>
                        </a>
                      )}
                    </Card>
                  </Col>
                </Row>
              </Timeline.Item>
            </Spin>
          );
        })}
    </Timeline>
  );
};

const Events = ({ hikeId, limit }: LastEventProps): JSX.Element => {
  // triggerEvents
  // hikeEvents
  // errorEvents

  const { triggerEvents } = useHikeRelatedEvents({
    hikeId,
    limit,
  });

  return <HikeEventsTimeLine events={triggerEvents} />;
  // return (
  //   <>
  //     {hikeRelatedEvents.map((resourceEvent: HikeRelatedEvents) => {
  //       return (
  //         <Event
  //           key={resourceEvent.id}
  //           resource={resourceEvent.resource}
  //           createdAt={resourceEvent.createdAt}
  //           hitch={resourceEvent.hitch}
  //         />
  //       );
  //     })}
  //   </>
  // );
};
export default Events;
